import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, FileSpreadsheet } from "lucide-react";
import { getRegistrationExportColumns, downloadAllRegistrationsExcel } from "@/lib/api";

const STORAGE_KEY = "mu.excelExportColumns";

const loadSaved = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const save = (keys) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch {
    /* storage unavailable — selection just isn't remembered */
  }
};

// Pick which registration columns go into the Excel. Uses the Registrations
// list's current filters; remembers the last selection in this browser.
export function ExcelColumnsDialog({ open, onOpenChange, filters, total }) {
  const [columns, setColumns] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open || columns.length) return;
    setLoading(true);
    getRegistrationExportColumns().then((res) => {
      setLoading(false);
      if (!res.ok) {
        toast.error(res.data?.detail || "Could not load columns");
        return;
      }
      const cols = res.data?.columns || [];
      setColumns(cols);
      const saved = loadSaved();
      const valid = new Set(cols.map((c) => c.key));
      const initial = saved ? saved.filter((k) => valid.has(k)) : cols.map((c) => c.key);
      setSelected(new Set(initial.length ? initial : cols.map((c) => c.key)));
    });
  }, [open, columns.length]);

  const groups = useMemo(() => {
    const out = [];
    columns.forEach((c) => {
      let g = out.find((x) => x.name === c.group);
      if (!g) out.push((g = { name: c.group, columns: [] }));
      g.columns.push(c);
    });
    return out;
  }, [columns]);

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const toggleGroup = (group, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      group.columns.forEach((c) => (on ? next.add(c.key) : next.delete(c.key)));
      return next;
    });

  const handleDownload = async () => {
    if (!selected.size) return toast.error("Select at least one column");
    // Keep catalog order so the sheet's columns read in a sensible sequence.
    const keys = columns.map((c) => c.key).filter((k) => selected.has(k));
    save(keys);
    setDownloading(true);
    const res = await downloadAllRegistrationsExcel(filters, keys.length === columns.length ? [] : keys);
    setDownloading(false);
    if (!res.ok) {
      toast.error(res.data?.detail || "Could not export registrations");
      return;
    }
    toast.success(`Downloading ${total} registrations · ${keys.length} columns`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="dialog-excel-columns">
        <DialogHeader>
          <DialogTitle>Export to Excel</DialogTitle>
          <DialogDescription>
            Choose the columns to include. Exports all {total} registrations matching the current filters.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between text-sm">
          <span className="text-[#6B7280]">
            <span className="font-medium text-[#111]">{selected.size}</span> of {columns.length} columns selected
          </span>
          <div className="flex gap-3">
            <button type="button" className="text-[#B60F2A] hover:underline font-medium" onClick={() => setSelected(new Set(columns.map((c) => c.key)))}>
              Select all
            </button>
            <button type="button" className="text-[#6B7280] hover:underline font-medium" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-5">
          {loading ? (
            <div className="py-10 text-center text-[#9CA3AF]">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading columns…
            </div>
          ) : (
            groups.map((g) => {
              const allOn = g.columns.every((c) => selected.has(c.key));
              return (
                <div key={g.name}>
                  <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allOn}
                      onChange={() => toggleGroup(g, !allOn)}
                      className="h-4 w-4 accent-[#B60F2A]"
                    />
                    {g.name}
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 pl-6">
                    {g.columns.map((c) => (
                      <label key={c.key} className="flex items-center gap-2 text-sm text-[#111] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selected.has(c.key)}
                          onChange={() => toggle(c.key)}
                          className="h-4 w-4 accent-[#B60F2A]"
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <button type="button" className="mu-btn-secondary text-sm" onClick={() => onOpenChange(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="mu-btn-primary text-sm"
            disabled={downloading || loading || !selected.size}
            onClick={handleDownload}
            data-testid="btn-excel-columns-download"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            {downloading ? "Preparing Excel…" : `Download Excel (${selected.size} columns)`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
